import type { KeyboardEvent, MouseEvent } from 'react';

import { SBOM_SHARING } from '../../../../shared/featureFlags/flagNames';
import { ProjectSource } from '../../../../typings/enums';
import { Action } from '../../../../typings/permissions';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { userHasPermissionForProject } from '../../../helpers/permissions';
import { icons } from '../../generic/Icon';
import { useCanDisplaySecurityOptions } from '../generic/hooks';
import SummaryActionOption from '../generic/SummaryActionOption';
import SummaryStatPanel from '../generic/SummaryStatPanel';
import type { ProjectDashboardProject, ProjectDashboardRevision } from '../projectDashboardTypes';

import ActionButton from './ProjectSummaryActionButton';
import SignInActionCard from './SignInActionCard';
import SummaryDependencyLink from './SummaryDependencyLink';

import styles from './StandardProjectSummaryActionCards.module.less';

type StandardProjectSummaryActionCardsProps = {
  project: ProjectDashboardProject;
  revision: ProjectDashboardRevision;
  isScannedForIssues: boolean;
  revisionBaseUrl: string;
  isRebuilding: boolean;
  onRebuild(event: MouseEvent | KeyboardEvent): void;
  onShowNoDependenciesModal(): void;
  onVisitAPIEndpoint(): void;
  onShareProject?(): void;
  routerPush?(path: string): void;
};

const StandardProjectSummaryActionCards = (props: StandardProjectSummaryActionCardsProps) => (
  <>
    <SummaryDependencyLink
      revision={props.revision}
      baseUrl={props.revisionBaseUrl}
      isScannedForIssues={props.isScannedForIssues}
      onShowNoDependenciesModal={props.onShowNoDependenciesModal}
    />
    <SummaryStatPanel
      count={props.revision.data?.license_count ?? 0}
      label="Licenses"
      icon={icons.checklist}
      to={`${props.revisionBaseUrl}/browse/licenses`}
      isLoading={!(props.isScannedForIssues || props.revision.data?.isSteady)}
    />
    {window.user ? <ActionCards {...props} /> : <SignInActionCard />}
  </>
);

// ActionCards is rendered for signed in users.
export const ActionCards = (props: StandardProjectSummaryActionCardsProps) => {
  const isSecurityAndPDFReportsEnabled = useCanDisplaySecurityOptions();
  const isSBOMSharingEnabled = useIsFeatureEnabled(SBOM_SHARING);
  return (
    <div className="card project_info__actions">
      <b className={styles.label}>Actions</b>
      <ul className="nav nav-pills nav-stacked">
        {!props.revision.data?.unsupported && (
          <>
            <li>
              <SummaryActionOption
                action="Generate Compliance Report"
                icon={icons.filePlusO}
                to={`${props.revisionBaseUrl}/reports/licensing`}
              />
            </li>
            {isSecurityAndPDFReportsEnabled && (
              <li>
                <SummaryActionOption
                  action="Generate Vulnerability Report"
                  icon={icons.filePlusO}
                  to={`${props.revisionBaseUrl}/reports/vulnerability`}
                />
              </li>
            )}
            <li>
              <SummaryActionOption
                action="Generate SBOM Report"
                icon={icons.filePlusO}
                to={`${props.revisionBaseUrl}/reports/sbom`}
              />
            </li>
          </>
        )}
        {/* TODO: warn if gen BOM w/ issues, and color it green with className="nav-success" if issueCount = 0 */}
        <li>
          <ActionButton icon={icons.api} text="Visit API Endpoint" onAction={props.onVisitAPIEndpoint} />
        </li>
        {userHasPermissionForProject(Action.Edit, props.project.data?.locator) && (
          <>
            {props.onShareProject && isSBOMSharingEnabled && (
              <li>
                <ActionButton
                  icon={icons.unfilledShare}
                  text="Share Project"
                  onAction={props.onShareProject}
                />
              </li>
            )}
            {!props.project.data?.require_mediated_dependencies &&
              props.project.data?.type !== ProjectSource.Binary && (
              <li>
                <ActionButton
                  text="Rebuild & Mediate Deps"
                  icon={icons.refresh}
                  iconHasSpin={props.isRebuilding}
                  onAction={props.onRebuild}
                />
              </li>
            )}
          </>
        )}
      </ul>
    </div>
  );
};

export default StandardProjectSummaryActionCards;
